import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, Input, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { QueryObjectsModel, QueryOutputMode } from '@barthauer/abc/core/data';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import { HighlightSyntax, _AbcTreeNode } from '@barthauer/abc/ui-components';
import { Subject } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';
import { AbcQueriesEditorService } from '../../../abc-queries-editor.service';
import { ResultsService } from '../../../services/result.service';
import { Property } from '../../../viewModels/abc-queries-editor.viewModel';

@Component({
    selector: 'abc-opath-queries-editor-results-xml',
    templateUrl: './results-xml.component.html',
    styleUrls: ['./results-xml.component.scss'],
    standalone: false
})
export class ResultsXmlComponent implements OnInit, OnDestroy {
  @Input() resultsEmpty: boolean;
  @ViewChild('code') code: ElementRef;

  public data: string;

  public executed: boolean = false;
  public loading: boolean = false;
  public resAsync: Promise<ResourceSet>;
  public translation: {
    popupErrorMessage: string;
    loading: string;
    resultMessage: {
      clickMessage: string;
      button: string;
      viewMessage: string;
    };
  };

  private messageBoxVisible: boolean = false;
  private queries: _AbcTreeNode<Property>;
  private $unsubscribe: Subject<void> = new Subject();
  private highlightSyntax: HighlightSyntax ;

  constructor(
    private resultService: ResultsService,
    private abcQueriesEditorService: AbcQueriesEditorService,
    private messageBoxService: MessageBoxService,
    private resourcesService: ResourceManagerService,
    private renderer: Renderer2
  ) {
    this.resAsync = this.resourcesService.getResourceSet('abc-opath-queries-editor');
  }

  async ngOnInit(): Promise<void> {
    this.highlightSyntax = new HighlightSyntax(this.renderer);

    this.translation = {
      popupErrorMessage: (await this.resAsync).get('result-ExecutionError'),
      loading: (await this.resAsync).get('result-loading'),
      resultMessage: {
        button: (await this.resAsync).get('menu-executeAsXML'),
        clickMessage: (await this.resAsync).get('result-ExecutePart1'),
        viewMessage: (await this.resAsync).get('result-ExecutePart2'),
      },
    };

    this.resultService.resultXmlCache && (this.executed = true);
  }

  async ngAfterViewInit(): Promise<void> {
    setTimeout(() => {
      this.code && (this.code.nativeElement.innerHTML = this.resultService.resultXmlCache);
    }, 0);
  }

  public async refreshQueriesData(): Promise<void> {
    this.loading = true;
    this.queries = this.abcQueriesEditorService.queries;

    if (this.queries.children.length) {
      await this.resultService.createObjectQueries(this.queries, this.abcQueriesEditorService.queryDocument);
      this.resultService.queryDocument
        .pipe(
          take(1),
          switchMap((xmlQuery: QueryObjectsModel) => {
            return this.resultService.executeQueries({ ...xmlQuery, OutputMode: QueryOutputMode.Xml });
          })
        )
        .subscribe(
          (data: string) => {
            this.data = data;
            this.executed = true;
            this.loading = false;
            if (data) {
              this.resultsEmpty = false;
            }
            setTimeout(() => {
              this.code.nativeElement.innerHTML = this.highlightSyntax.highlight('xml', data).value;
              this.resultService.resultXmlCache = this.code.nativeElement.innerHTML;
            }, 0);
          },
          async (response: HttpErrorResponse) => {
            !this.messageBoxVisible && (await this.showErrorMessage(response));
            this.resultService.resultXmlCache = '';
            this.executed = false;
          }
        );
    } else {
      this.loading = false;
    }
  }

  private async showErrorMessage(response: HttpErrorResponse): Promise<void> {
    this.messageBoxVisible = true;
    this.executed = false;
    this.loading = false;

    const settings = new MessageSettings();
    settings.mode = MessageMode.Error;
    settings.icon = MessageBoxIcon.Error;
    settings.button = MessageBoxButtons.OK;
    settings.message = response.error.Message;
    if (response.error.InnerException) {
      settings.errorMessage =
        response.error.InnerException.Message +
        `\n\n` +
        response.error.InnerException.ExceptionType +
        `\n\n` +
        response.error.InnerException.ExceptionMessage;
    }

    this.messageBoxService
      .showMessageBox(settings)
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe((result: DialogResult) => {
        if (DialogResult.OK === result['result']) {
          this.messageBoxVisible = false;
          return;
        }
      });
  }

  ngOnDestroy(): void {
    this.$unsubscribe.next(null);
    this.$unsubscribe.complete();
  }
}
